import { RFHeader, NavType, ExplicitlyHideSearchBar } from '@chegg-me-components/header'
import { Footer } from '@chegg-me-components/footer'
import {
  SecondaryNavLinks,
  SignedOutHeader,
} from '@chegg-me-components/signed-out-header'
import { useDefaultProps } from '@chegg-me-components/signed-out-header-props'

import { Config } from '@chegg-wt/core/config'
import { AppState } from '@chegg-wt/core/state'
import React from 'react'
import { connect } from 'react-redux'
import { pipe } from 'rxjs'
import { routeToHref, addRedirectQueryToRoute } from '@chegg-wt/core/utils/routes'
import { useCheggLayout } from '@chegg-wt/core/components/CheggLayout/CheggLayoutProvider'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import { isWindowDefined } from '@chegg-wt/core/utils/isWindowDefined'
import useHasMounted from '@chegg-wt/core/hooks/useHasMounted'
import { initializedSelector } from '@chegg-wt/core/state/app/selectors'
import { isGuestSelector, userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { useRoutesContext } from '../RoutesContext'
import { LvpMarginWrapper } from '../Layouts/LargeVideoPlayerLayout'
import * as DomIds from './domIds'
import * as Styled from './styled'
import * as TestIds from './testIds'
import { CheggLayoutProps, CheggLayoutStateProps } from './types'

type Props = CheggLayoutProps & CheggLayoutStateProps

// remove leading slash
const sanitizeUrl = (url: string) => url.replace(/^\/+/, '')

export const CheggLayout: React.FC<Props> = ({
  children,
  initialized,
  isGuest,
  isCheggFree,
  waitForInitialization,
  tourHandler,
  skipDataQuery = false,
}) => {
  const { user, pageTrackId, serverRenderAsMobile } = useCheggLayout()

  const routes = useRoutesContext()
  const hasMounted = useHasMounted()
  const signedOutHeaderDefaultProps = useDefaultProps()

  const secondaryNavLinks: SecondaryNavLinks = [
    {
      text: 'For educators',
      href: 'uversity',
    },
    {
      text: 'Sign in',
      href: isWindowDefined
        ? sanitizeUrl(
            routeToHref(addRedirectQueryToRoute(routes.toLogin(), window.location.href))
          )
        : pipe(routes.toLogin, routeToHref, sanitizeUrl)({}),
    },
    {
      text: 'Help',
      href: 'contactus',
    },
  ]

  let headerPosition = 'fixed'
  if (hasMounted && tourHandler?.active) {
    headerPosition = 'relative'
  }
  if (isCheggFree) {
    headerPosition = 'absolute'
  }

  const Header = () =>
    isGuest ? (
      <SignedOutHeader
        {...signedOutHeaderDefaultProps}
        envUrl={`${Config.cheggEnvUrl}/`}
        secondaryNavLinks={secondaryNavLinks}
        serverRenderAsMobile={serverRenderAsMobile}
        mainContentId={DomIds.Main}
        data-test={TestIds.CHEGG_LAYOUT_HEADER}
      />
    ) : (
      <RFHeader
        navType={NavType.writing}
        envUrl={`${Config.cheggEnvUrl}/`}
        user={user}
        skipDataQuery={skipDataQuery}
        serverRenderAsMobile={serverRenderAsMobile}
        pageTrackId={pageTrackId}
        mainContentId={DomIds.Main}
        data-test={TestIds.CHEGG_LAYOUT_HEADER}
        headerDataSource={Config.headerDataSource}
      >
        {ExplicitlyHideSearchBar}
      </RFHeader>
    )

  return (
    <>
      <Styled.CheggHeaderWrapper headerPosition={headerPosition}>
        <Header />
      </Styled.CheggHeaderWrapper>
      <Styled.Content id={DomIds.Main} isGuest={isGuest}>
        <div>
          {waitForInitialization && !initialized ? (
            <LoadingDots verticalPadding={60} message="Loading page" />
          ) : (
            children
          )}
        </div>
        <LvpMarginWrapper>
          <Footer
            envUrl={`${Config.cheggEnvUrl}/`}
            pageTrackId={pageTrackId}
            serverRenderAsMobile
          />
        </LvpMarginWrapper>
      </Styled.Content>
    </>
  )
}

const mapStateToProps = (state: AppState): CheggLayoutStateProps => ({
  initialized: initializedSelector(state),
  isGuest: isGuestSelector(state) || userRoleSelector(state) === '',
})

export default connect<CheggLayoutStateProps>(mapStateToProps)(CheggLayout)
